"use client";

import toast, { Toaster } from "react-hot-toast";

import React, {
  lazy,
  Suspense,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import "./App.css";
import "./darkTheme.css";
import "./styles/portal-theme.css";
import "./styles/themes.css";
import "./styles/dev-theme.css";
import "./styles/dev-dark-theme.css";
import { useDispatch, useSelector } from "react-redux";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import ScreenLoader from "./components/common/ScreenLoader";
import { callApi } from "./components/common/Apicall";
import { addData } from "./components/Slices/DataSlice";
import { registerNavigate, SCREEN_PATHS, pathForScreen, buildUrl } from "./components/common/navigation";
import AppLayout from "./components/common/AppLayout";
import { isCOO, isPresident } from "./components/common/privileges";
import ChatIconButton from "./components/common/ChatIconButton";
import useGetConversations from "./components/hooks/useGetConversations";
import { initiateSignalR } from "./components/SingalR";
import { openSocket } from "./components/Slices/SocketSlice";
import { setAnnouncements } from "./components/Slices/AnnouncementSlice";
import { setLoading } from "./components/Slices/SidebarDataLoadingSlice";
import CustomModal from "./components/common/CustomModal";
import TemporaryPasswordChange from "./components/Header/TemporaryPasswordChange";
import { setAuth } from "./components/Slices/AuthSlice";
import useSoundInitializer from "./components/hooks/useSoundInitializer";

// Lazy load components
const Login = lazy(() => import("./components/Login/Login"));
const SearchedTicket = lazy(() =>
  import("./components/SearchedTicket/SearchedTicket")
);
const Main = lazy(() => import("./components/AddScreens/Main/Main"));
const Attendance = lazy(() => import("./components/attendance/Attendance"));
const Checklist = lazy(() => import("./components/checklist/Checklist"));
const ChecklistAnalytics = lazy(() =>
  import("./components/checklist/ChecklistAnalytics")
);
const SoftwareAnalytics = lazy(() =>
  import("./components/software/software-analytics/ParentAnalytics")
);
const SoftwareTickets = lazy(() =>
  import("./components/software/software-tickets/SoftwareTickets")
);
const ActivePractices = lazy(() =>
  import("./components/software/active-practices/ActivePractices")
);
const ProjectStatus = lazy(() =>
  import("./components/software/project-status/ProjectStatus")
);

const ProSoftwareTeam = lazy(() =>
  import("./components/software/prosoftware-team/ProSoftwareTeam")
);
const SupportAnalytics = lazy(() =>
  import("./components/support/support-analytics/SupportAnalytics")
);
const SupportTickets = lazy(() =>
  import("./components/support/support-tickets/SupportTickets")
);
const Dashboard = lazy(() => import("./components/Header/Dashboard/Dashboard"));
const ChangeRequestForm = lazy(() =>
  import("./components/change-request-form/ChangeRequestFrom")
);
const ManagementBoard = lazy(() =>
  import("./components/managementBoard/ManagementBoard")
);
// Service ID: #3867 — Biller report screen entry point.
const BillerSummary = lazy(() =>
  import("./components/biller-summary/BillerSummary")
);

// Dropdown/reference data (projects, assignees, priorities, etc.) rarely
// changes minute-to-minute, so cached data younger than this is reused
// instantly instead of blocking on a fresh fetch.
const PRACTICE_DATA_TTL_MS = 15 * 60 * 1000; // 15 minutes

const initialImportModel = {
  perPage: 20,
  pageNo: 1,
  practiceId: null,
  totalPages: 0,
  totalCount: 0,
  teamId: null,
  totalRows: "",
  id: "",
  priorityId: "",
  taskResponsibilityId: "",
  categoryId: "",
  name: "",
  statusId: "",
  createdFrom: "",
  createdTo: "",
};

const App = () => {
  const [state, setState] = useState({
    importModel: initialImportModel,
    addPopup: "",
    totalRows: 50,
    rows: [],
    roles: [],
    modalRow: null,
  });

  const dispatch = useDispatch();
  const message = useSelector((state) => state.data?.userInfo?.data);
  // President users land on BMR Engineer Summary; COO users land on the
  // Biller Summary rollup; other BMR users land on the default ProSoftware
  // service tickets tab.
  const isPresidentUser = isPresident(message?.data?.EmpId);
  const isCooUser = isCOO(message?.data?.EmpId);
  // Service ID: #3867 — COO post-login destination is the biller report.
  const postLoginPath = isPresidentUser
    ? buildUrl("BMRSoftwareTickets", { tab: "EngineerSummary" })
    : isCooUser
      ? buildUrl("BillerSummary", { tab: "Biller Summary" })
      : message?.data?.RoleId === 14
        ? buildUrl("BMRSoftwareTickets", { tab: "EngineerSummary" })
        : buildUrl("Dashboard");
  const connection = useSelector((state) => state.socket.socketConnection);
  // Dropdown/reference data persisted from a previous session (via
  // redux-persist) — used to decide whether we can skip straight to a
  // stale-while-revalidate render instead of blocking on a fresh fetch.
  const cachedPractice = useSelector((state) => state.data?.practice);
  const practiceUpdatedAt = useSelector((state) => state.data?.practiceUpdatedAt);
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [hasTempPassword, setHasTempPassword] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [isDepartmentLoaded, setIsDepartmentLoaded] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null); // Track current user ID
  // Guards checkTempPassword against firing its GetUserById call more than
  // once per user per session.
  const tempPasswordCheckedForUserRef = useRef(null);
 useSoundInitializer();
  const URL = process.env.REACT_APP_URL;
  // ChatIconButton (and its unread-count badge) renders globally once
  // authenticated — see the comment above its render below — so the
  // conversations fetch that feeds it must not be gated to a specific
  // route either; it used to require `location.pathname ===
  // SCREEN_PATHS.Dashboard`, which meant the badge stayed empty for any
  // user who ever landed somewhere other than /dashboard after login
  // (e.g. President users, who land on the BMR route).
  const shouldFetchConversations = !!message?.token;

  // Expose the router navigator to non-React modules (e.g. Apicall.js 401 handler)
  useEffect(() => {
    registerNavigate(navigate);
  }, [navigate]);

  // Define handleChange first
  const handleChange = useCallback((name, value) => {
    setState((prev) => ({
      ...prev,
      importModel: {
        ...prev.importModel,
        [name]: value?.toString(),
      },
    }));

    if (name !== "priorityId") {
      trigger();
    }
  }, []);

  const trigger = useCallback(() => {
    navigate(pathForScreen("SearchedTicket"));
  }, [navigate]);

  // Reset all states when user changes
  const resetAppState = useCallback(() => {
    setState({
      importModel: initialImportModel,
      addPopup: "",
      totalRows: 50,
      rows: [],
      roles: [],
      modalRow: null,
    });
    setLoader(false);
    setIsProfileModalOpen(false);
    setHasTempPassword(false);
    setCurrentUserData(null);
    setIsDepartmentLoaded(false);
    setInitialLoadComplete(false);
    tempPasswordCheckedForUserRef.current = null;
  }, []);

  // Props shared by the Dashboard and SearchedTicket screens
  const dashboardProps = {
    handleChange,
    dashboardstate: { ...state },
    setdashboardstate: { setState },
  };

  // SignalR connection effect
  useEffect(() => {
    if (!message?.token) return;

    const connect = initiateSignalR(message);
    dispatch(openSocket(connect));

    return () => {
      if (connect) connect.stop();
    };
  }, [message, dispatch]);

  // Check for temporary password
  const checkTempPassword = useCallback(async () => {
    try {
      const userId = message?.data?.UserId;
      if (!userId) return;
      // Already checked this user this session — skip the redundant call.
      if (tempPasswordCheckedForUserRef.current === userId) return;
      tempPasswordCheckedForUserRef.current = userId;

      const userResponse = await callApi(
        `${URL}/Users/GetUserById/${userId}`,
        "GET"
      );

      setCurrentUserData(userResponse);

      if (
        userResponse?.password === "welcome@123" ||
        userResponse?.password === "Welcome@123"
      ) {
        setHasTempPassword(true);
        setIsProfileModalOpen(true);
      }
    } catch (error) {
      console.error("Error checking temporary password:", error);
      // Allow a retry on the next call since this attempt failed.
      tempPasswordCheckedForUserRef.current = null;
    }
  }, [URL, message?.data?.UserId]);

  // Fetch all initial data
  const getData = useCallback(async () => {
    const userId = message?.data?.UserId;
    if (!message?.token || !userId) return;

    // If this is the same user and data is already loaded, don't reload
    if (currentUserId === userId && initialLoadComplete) return;

    // Stale-while-revalidate: if redux-persist already rehydrated
    // reasonably-fresh dropdown data from a previous session, use it right
    // away — no loader, no wait — and quietly refetch in the background to
    // keep it current instead of blocking the user on it again.
    const isCacheFresh =
      practiceUpdatedAt &&
      Date.now() - practiceUpdatedAt < PRACTICE_DATA_TTL_MS &&
      cachedPractice &&
      Object.keys(cachedPractice).length > 0;

    if (isCacheFresh) {
      setIsDepartmentLoaded(true);
      setInitialLoadComplete(true);
      setCurrentUserId(userId);
    } else {
      setLoader(true);
      dispatch(
        setLoading({ isLoading: true, message: "Loading department data..." })
      );
      setIsDepartmentLoaded(false);
    }

    try {
      const [
        deptResponse,
        assigneesResponse,
        practiceDataResponse,
        responsibilityResponse,
        announcementsResponse,
      ] = await Promise.all([
        callApi(`${URL}/Dashboard/GetAllDropDown`, "get"),
        callApi(`${URL}/Dashboard/GetAllFilteredAssignees`, "get"),
        callApi(`${URL}/Practice/GetPracticeDropDown`, "get"),
        callApi(`${URL}/Responsibility/GetResponsibilityDropDown`, "get"),
        callApi(`${URL}/Announcements/GetAnnouncements`, "GET"),
      ]);
      const combinedData = {
        ...deptResponse.sideBarOptions,
        ...assigneesResponse.sideBarOptions,
        ...practiceDataResponse.sideBarOptions,
        ...responsibilityResponse.sideBarOptions,
      };
      dispatch(addData({ name: "practice", data: combinedData }));
      dispatch(addData({ name: "practiceUpdatedAt", data: Date.now() }));
      dispatch(setAnnouncements(announcementsResponse?.announcements));

      setIsDepartmentLoaded(true);
      setInitialLoadComplete(true);
      setCurrentUserId(userId); // Set current user ID after successful load
    } catch (error) {
      console.error("Error fetching data:", error);
      // If we already rendered from fresh cache, a background refresh
      // failure shouldn't take the user back to a broken/loading state.
      if (!isCacheFresh) {
        setIsDepartmentLoaded(false);
        setInitialLoadComplete(true);
        toast.error(
          "Failed to load some data. You can continue with limited functionality."
        );
      }
    } finally {
      setLoader(false);
      dispatch(setLoading({ isLoading: false }));
    }
  }, [
    URL,
    dispatch,
    message?.token,
    message?.data?.UserId,
    currentUserId,
    initialLoadComplete,
    cachedPractice,
    practiceUpdatedAt,
  ]);

  // Effect to handle user changes and login
  useEffect(() => {
    const userId = message?.data?.UserId;

    // If no token, reset everything
    if (!message?.token) {
      resetAppState();
      setCurrentUserId(null);
      return;
    }

    // If user changed, reset state and load new data
    if (userId && userId !== currentUserId) {
      resetAppState();
      setCurrentUserId(userId);

      // Small delay to ensure state is reset
      setTimeout(() => {
        getData();
        checkTempPassword();
      }, 100);
    }
    // If same user and login message, ensure data is loaded
    else if (
      message?.message === "Login Successfully" &&
      userId === currentUserId &&
      !initialLoadComplete
    ) {
      getData();
      checkTempPassword();
    }
  }, [
    message,
    currentUserId,
    getData,
    checkTempPassword,
    resetAppState,
    initialLoadComplete,
  ]);

  // Handle logout
  const handleLogout = useCallback(() => {
    if (connection) {
      connection.stop();
    }

    setIsProfileModalOpen(false);

    // Reset all app state
    resetAppState();
    setCurrentUserId(null);

    toast.remove();

    connection?.stop();
    dispatch(setAuth(false));
    dispatch(addData({ name: "practice", data: [] }));
    dispatch(addData({ name: "practiceUpdatedAt", data: null }));
    dispatch(addData({ name: "userInfo", data: [] }));

    // Clear per-user display data so it doesn't leak to the next user who
    // logs in on this device (these are set outside Redux in Login.jsx /
    // UserProfileBody.jsx and read in Header/Index.js).
    localStorage.removeItem("profilePicture");
    localStorage.removeItem("fullName");

    navigate(pathForScreen("Login"));
  }, [connection, dispatch, resetAppState, navigate]);

  const toggleProfileModal = useCallback(() => {
    setIsProfileModalOpen((prev) => !prev);
  }, []);

  // Get conversations if needed
  useGetConversations(shouldFetchConversations);

  return (
    <>
      <Toaster
        toastOptions={{
          duration: 5000,
          className: "app-toast",
        }}
      />
      <div className="flex flex-col min-h-screen font-sans bg-white">
        <Suspense fallback={<ScreenLoader  />}>
          {!message?.token ? (
            <Routes>
              <Route path={SCREEN_PATHS.Login} element={<Login />} />
              <Route
                path="*"
                element={<Navigate to={SCREEN_PATHS.Login} replace />}
              />
            </Routes>
          ) : (
            // Dropdown/reference data (projects, assignees, priorities, etc.)
            // loads in the background via getData() — routes render right
            // away instead of blocking on it. Screens that consume that data
            // already tolerate it arriving late (empty options until Redux
            // updates), and the ticket-form sidebar shows its own small
            // loader (sidebarDataLoading) while it's still in flight.
            <Routes>
              {/* ChecklistAnalytics renders headerless on purpose: it is
                  primarily a tab inside ParentAnalytics (which supplies the
                  surrounding flex layout), so it stays outside AppLayout. */}
              <Route
                path={SCREEN_PATHS.ChecklistAnalytics}
                element={<ChecklistAnalytics />}
              />
              {/* Every screen below shares one persistent app header, mounted
                  once by AppLayout so it survives navigation instead of
                  remounting on each route change. */}
              <Route element={<AppLayout />}>
                <Route
                  path={SCREEN_PATHS.Dashboard}
                  element={<Dashboard {...dashboardProps} />}
                />
                <Route
                  path={SCREEN_PATHS.SearchedTicket}
                  element={<SearchedTicket {...dashboardProps} />}
                />
                {/* Same component as SearchedTicket — reached from the BMR
                    menu instead of the Client menu, see navigation.js. */}
                <Route
                  path={SCREEN_PATHS.BMRSearchedTicket}
                  element={<SearchedTicket {...dashboardProps} viewContext="bmr" />}
                />
                <Route path={SCREEN_PATHS.Main} element={<Main />} />
                <Route path={SCREEN_PATHS.Attendance} element={<Attendance />} />
                <Route path={SCREEN_PATHS.Checklist} element={<Checklist />} />
                <Route
                  path={SCREEN_PATHS.SoftwareAnalytics}
                  element={<SoftwareAnalytics />}
                />
                <Route
                  path={SCREEN_PATHS.SoftwareTickets}
                  element={<SoftwareTickets />}
                />
                {/* Same component as SoftwareTickets — reached from the BMR
                    menu instead of the Software menu, see navigation.js. */}
                <Route
                  path={SCREEN_PATHS.BMRSoftwareTickets}
                  element={<SoftwareTickets viewContext="bmr" />}
                />
                <Route
                  path={SCREEN_PATHS.ActivePractices}
                  element={<ActivePractices />}
                />
                <Route
                  path={SCREEN_PATHS.ProjectStatus}
                  element={<ProjectStatus />}
                />
                <Route
                  path={SCREEN_PATHS.ProSoftwareTeam}
                  element={<ProSoftwareTeam />}
                />
                <Route
                  path={SCREEN_PATHS.SupportAnalytics}
                  element={<SupportAnalytics />}
                />
                <Route
                  path={SCREEN_PATHS.SupportTickets}
                  element={<SupportTickets />}
                />
                <Route
                  path={SCREEN_PATHS.ChangeRequestForm}
                  element={<ChangeRequestForm />}
                />
                <Route
                  path={SCREEN_PATHS.ManagementBoard}
                  element={<ManagementBoard />}
                />
                {/* Service ID: #3867 — Biller report route registration. */}
                <Route
                  path={SCREEN_PATHS.BillerSummary}
                  element={<BillerSummary />}
                />
              </Route>
              <Route
                path={SCREEN_PATHS.Login}
                element={<Navigate to={postLoginPath} replace />}
              />
              <Route
                path="*"
                element={<Navigate to={postLoginPath} replace />}
              />
            </Routes>
          )}
        </Suspense>

        {/* Render ChatIconButton once authenticated — it only reads from the
            conversation slice, not the dropdown/reference data, so it
            doesn't need to wait on initialLoadComplete either. */}
        {message?.token && <ChatIconButton />}
      </div>

      {/* Profile Image Modal */}
      {isProfileModalOpen && (
        <CustomModal
          isLoading={false}
          title="Change Password"
          size="md"
          ModalBody={
            currentUserData ? (
              <TemporaryPasswordChange
                onClose={toggleProfileModal}
                currentUserData={currentUserData}
              />
            ) : null
          }
          onClose={handleLogout}
        />
      )}
    </>
  );
};

export default React.memo(App);
